import { Modal, Stack, Table } from 'react-bootstrap';

import CheckmateSelect from '../../shared/CheckmateSelect';
import { IMatchedCasesSearchResponseModel } from '../../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';
import { IMultiSelectOption } from '../../../interfaces/ILookup';
import { IValidation } from '../../../interfaces/IError';
import { Link } from 'react-router-dom';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useState } from 'react';

export interface ISameCaseDetailsSelectorModalProps {
    newlySelectedCases: IMatchedCasesSearchResponseModel[];
    existingCaseGroupGuid?: string;
    currentCaseGroupName?: string;
    currentCaseGroupNumber?: string;
    distinctNames: string[];
    distinctNumbers: string[];
    onCancelClick: () => void;
    onSubmitClick: (caseGroupName: string, caseGroupNumber: string, caseGroupGuid?: string | null) => void;
    validation?: IValidation;
}

export function SameCaseNewCaseModal(props: ISameCaseDetailsSelectorModalProps) {
    const { user } = useAuthContext();
    const [caseGroupNumber, setCaseGroupNumber] = useState<string>(
        props.currentCaseGroupNumber ?? ''
    );
    const [caseGroupName, setCaseGroupName] = useState<string>(props.currentCaseGroupName ?? '');

    const handleCaseGroupNameChange = (selectedOption: { value: string; label: string } | null) => {
        if (selectedOption) {
            setCaseGroupName(selectedOption.value);
        }
    };

    const handleCaseGroupNumberChange = (
        selectedOption: { value: string; label: string } | null
    ) => {
        if (selectedOption) {
            setCaseGroupNumber(selectedOption.value);
        }
    };

    const handleSaveSubmitClick = () => {
        props.onSubmitClick(caseGroupName, caseGroupNumber, props.existingCaseGroupGuid);
    };

    const handleNewSubmitClick = () => {
        props.onSubmitClick(caseGroupName, caseGroupNumber, null);
    };

    const distinctCaseNameOptions = props.distinctNames.map(
        (name, idx) =>
            ({
                value: name,
                label: name,
                id: idx,
            }) as IMultiSelectOption
    );

    const distinctCaseNumberOptions = props.distinctNumbers.map(
        (number, idx) =>
            ({
                value: number,
                label: number,
                id: idx,
            }) as IMultiSelectOption
    );

    const showCaseNameandCaseNumberSelectors =
        props.distinctNames.length > 1 || props.distinctNumbers.length > 1;

    return (
        <Modal centered show={true} backdrop size="lg">
            <Modal.Header>
                <Modal.Title>Save or Create New Same Case Group</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <span className="text-danger">{props.validation?.model}</span>
                </div>
                <p>
                    Would you like to create a new Same Case Group below, or add them to the
                    current Same Case Group?
                </p>
                <Stack gap={3}>
                    <Table borderless>
                        <thead>
                            <tr>
                                <th style={{ width: 120 }}>Zone</th>
                                <th style={{ width: 180 }}>Case Name</th>
                                <th style={{ width: 120 }}>Case Number</th>
                                <th style={{ width: 180 }}>Jurisdiction</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.newlySelectedCases?.map((newlySelectedCase) => {
                                const userHasAccessToZone = user.organizationRoles?.some(
                                    (orgRole) => orgRole.guid === newlySelectedCase.organizationGuid
                                );
                                const caseUrl = `/zone/${newlySelectedCase.organizationGuid}${LocalRoutes.CaseFile.replace(
                                    ':guid',
                                    newlySelectedCase.caseGuid
                                )}`;

                                return (
                                    <tr
                                        key={newlySelectedCase.caseGuid}
                                        style={{ verticalAlign: 'middle' }}
                                    >
                                        <td>{newlySelectedCase.organizationName}</td>
                                        <td>
                                            {userHasAccessToZone ? (
                                                <Link to={caseUrl} target="_blank">
                                                    {newlySelectedCase.caseName}
                                                </Link>
                                            ) : (
                                                <>{newlySelectedCase.caseName}</>
                                            )}
                                        </td>
                                        <td>{newlySelectedCase.caseNumber}</td>
                                        <td>{newlySelectedCase.jurisdiction}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Stack>
                {showCaseNameandCaseNumberSelectors &&
                <Stack gap={3}>
                    <div>
                        <div>Case Name</div>
                        <CheckmateSelect
                            onChange={handleCaseGroupNameChange}
                            options={distinctCaseNameOptions}
                            value={distinctCaseNameOptions.find(
                                (opt) => opt.value === caseGroupName
                            )}
                            isDisabled={props.distinctNames.length <= 1}
                        />
                    </div>
                    <div>
                        <div>Case Number</div>
                        <CheckmateSelect
                            onChange={handleCaseGroupNumberChange}
                            options={distinctCaseNumberOptions}
                            value={distinctCaseNumberOptions.find(
                                (opt) => opt.value === caseGroupNumber
                            )}
                            isDisabled={props.distinctNumbers.length <= 1}
                        />
                    </div>
                </Stack>}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-orange" onClick={handleNewSubmitClick}>
                    Create New
                </button>
                <button className="btn btn-orange" onClick={handleSaveSubmitClick}>
                    Add To Existing
                </button>
                <button className="btn btn-default" onClick={props.onCancelClick}>
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
}
